import { useState, useRef, useEffect } from "react";
import type { InlineTypeWidgetComponentProps } from "@easyblocks/core";
import { backgroundClassOptions } from "../utils/easyblocks-config/options";

export function BackgroundColorWidget(props: InlineTypeWidgetComponentProps<string>) {
  const selectedValue = props?.value ?? "text-bg-white";
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = backgroundClassOptions.find((opt) => opt.value === selectedValue);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 300; // maxHeight of dropdown
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If not enough space below and more space above, open upward
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      const margin = 20;
      const dropdownWidth = 180;
      let right = window.innerWidth - rect.right;
      if (right < margin) right = margin;
      if (right > window.innerWidth - dropdownWidth - margin) right = window.innerWidth - dropdownWidth - margin;
      setDropdownPosition({
        top: openUpward ? rect.top - dropdownHeight - 2 : rect.bottom + 2,
        right,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (value: string) => {
    props.onChange(value);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="position-relative w-100 d-flex justify-content-end" style={{ maxWidth: "100%" }}>
      {/* Selected value display */}
      <button
        ref={buttonRef}
        type="button"
        className={`${selectedValue} border text-start d-flex align-items-center color-widget-button`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{ cursor: "pointer", fontWeight: "normal", fontSize: "12px", width: "120px", height: "32px", borderRadius: "2px", paddingLeft: "8px", paddingRight: "8px", position: "relative" }}
      >
        <span style={{ flex: 1 }}>{selectedOption?.label ?? "Select color"}</span>
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
          <path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="#999999" fillRule="evenodd" clipRule="evenodd"></path>
        </svg>
      </button>

      {/* Dropdown options */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="bg-white border shadow-sm p-1 color-widget-dropdown"
          role="listbox"
          aria-label="Background color options"
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            minWidth: "120px",
            width: "auto",
            maxWidth: "180px",
            maxHeight: "300px",
            overflowY: "auto",
            overflowX: "hidden",
            zIndex: 9999,
            borderRadius: "2px",
          }}
        >
          {backgroundClassOptions.map((opt) => {
            const isSelected = opt.value === selectedValue;

            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected ? "true" : "false"}
                className={`${opt.value} w-100 border-0 text-wrap fs-12 mb-1 d-flex align-items-center gap-2 color-widget-dropdown-option ${opt.value === "text-bg-white" || opt.value === "bg-transparent" ? "color-widget-option-white" : "color-widget-option"}`}
                onClick={() => handleSelect(opt.value)}
                style={{
                  cursor: "pointer",
                  fontWeight: "normal",
                  height: "32px",
                  padding: "0 8px",
                  textAlign: "left",
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>{opt.label}</span>
                <span className="color-widget-selected-indicator-slot" data-visible={isSelected ? "true" : "false"} aria-hidden="true">
                  <span className="color-widget-selected-indicator">✓</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BackgroundColorWidget;

